import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Bus, Calendar, LucideAngularModule, MapPin, Phone, Plane, Ship } from 'lucide-angular';
import type { LinksItemModel } from '../../../server/models/links-item-model';
import { AuthService } from '../../core/auth/services/auth.service';
import {
  type EventItem,
  type EventType,
  type EventWithAgendaItems,
  type EventWithLinkItems,
  isEventWithAgendaField,
  isEventWithLinks,
  isEventWithTravelInformation,
} from '../../core/models/middleware/event-item.model';
import type { UserItem } from '../../core/models/middleware/user-item.model';
import type { OffsiteItemModel } from '../../core/models/offsite-item.model';
import { EventService } from '../../core/services/event.service';
import { LinkBoxComponent } from '../../shared/components/link-box/link-box.component';
import { MarkdownCardComponent } from '../../shared/components/markdown-card/markdown-card.component';
import { ProgressiveImageComponent } from '../../shared/components/progressive-image/progressive-image.component';
import { SharedCardComponent } from '../../shared/components/shared-card/shared-card.component';
import { UpdatesComponent } from '../updates/updates.component';

@Component({
  selector: 'app-overview',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    SharedCardComponent,
    MarkdownCardComponent,
    ProgressiveImageComponent,
    UpdatesComponent,
    LinkBoxComponent,
  ],
  templateUrl: './overview.component.html',
})
export class OverviewComponent {
  // Icons
  readonly events = Calendar;
  readonly mapPin = MapPin;
  readonly phone = Phone;
  readonly plane = Plane;
  readonly ship = Ship;
  readonly bus = Bus;

  hasUpdates = false;

  // Computed properties for Strapi data
  eventTitle = computed(() => {
    const currentEvent = this.event();
    return currentEvent?.title || '';
  });
  eventLocation = computed(() => {
    const currentEvent = this.event();
    if (currentEvent && 'city' in currentEvent && 'country' in currentEvent) {
      return `${currentEvent.city}, ${currentEvent.country}`;
    }
    return '';
  });
  eventStartDate = computed(() => {
    const currentEvent = this.event();
    return currentEvent?.startDate;
  });
  eventEndDate = computed(() => {
    const currentEvent = this.event();
    return currentEvent?.endDate;
  });
  // Helper to check if end date is different from start date
  isMultiDay = computed(() => {
    const startDate = this.eventStartDate();
    const endDate = this.eventEndDate();
    if (!startDate || !endDate) return false;

    const start = new Date(startDate);
    const end = new Date(endDate);

    return start.toDateString() !== end.toDateString();
  });
  eventDateCompact = computed(() => {
    const startDate = this.eventStartDate();
    const endDate = this.eventEndDate();
    if (!startDate) return '';

    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;

    // Format start date compactly with weekday
    const startWeekday = start.toLocaleDateString('de-DE', { weekday: 'short' });
    const startDay = start.getDate().toString().padStart(2, '0');
    const startMonth = (start.getMonth() + 1).toString().padStart(2, '0');
    let result = `${startWeekday}, ${startDay}.${startMonth}`;

    // If multi-day, add end date with year and weekday
    if (end && start.toDateString() !== end.toDateString()) {
      const endWeekday = end.toLocaleDateString('de-DE', { weekday: 'short' });
      const endDay = end.getDate().toString().padStart(2, '0');
      const endMonth = (end.getMonth() + 1).toString().padStart(2, '0');
      const endYear = end.getFullYear().toString().slice(-2);
      result += ` - ${endWeekday}, ${endDay}.${endMonth}.${endYear}`;
    }

    return result;
  });
  phoneNumbers = computed(() => {
    const currentEvent = this.event();
    if (currentEvent && 'phoneNumbers' in currentEvent) {
      const offsiteEvent = currentEvent as OffsiteItemModel;
      return offsiteEvent.phoneNumbers || [];
    }
    return [];
  });
  travelInformation = computed(() => {
    const currentEvent = this.event();
    if (currentEvent && isEventWithTravelInformation(currentEvent)) {
      return currentEvent.travelInformation;
    }
    return undefined;
  });

  moodImageData = computed(() => {
    const currentEvent = this.event();
    if (currentEvent?.moodImage) {
      return {
        url: currentEvent.moodImage.url,
        thumbnailUrl: currentEvent.moodImage.thumbnailUrl,
        width: currentEvent.moodImage.width,
        height: currentEvent.moodImage.height,
        alt: `${currentEvent.title} mood image`,
      };
    }
    return null;
  });
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  currentUser = signal<UserItem | undefined>(this.authService.currentUser() || undefined);
  private eventService = inject(EventService);
  event = computed(() => this.eventService.getCurrentEvent());
  agenda = computed(() => {
    const currentEvent = this.eventService.getCurrentEvent();
    if (currentEvent && isEventWithAgendaField(currentEvent)) {
      return (currentEvent as EventWithAgendaItems<EventType>).agenda;
    } else {
      return [];
    }
  });
  // Event data
  eventDocumentId = this.route.snapshot.params.eventDocumentId as string;
  private eventType: EventType = this.route.snapshot.params.eventType;

  constructor() {
    if (this.eventService.getCurrentEvent()?.documentId === this.eventDocumentId) {
      // Display stored event until the refresh is done
      this.eventService.setCurrentEvent(this.eventService.getCurrentEvent() as EventWithAgendaItems<EventType>);
    } else {
      this.eventService.clearCurrentEvent();
    }

    this.eventService.getEvent(this.eventDocumentId, this.eventType).subscribe({
      next: (data) => {
        if (data) {
          this.eventService.setCurrentEvent(data);
        } else {
          // If we don't receive event data, we need to clear old data, to prevent us from displaying the wrong data, should never trigger as empty Observables are complete and next is never called
          this.eventService.clearCurrentEvent();
        }
      },
      error: (error) => {
        this.eventService.clearCurrentEvent();
        // TODO [#215]: use winston for logging
        console.error(`Error loading ${this.eventType} event:`, error);
        throw error;
      },
      complete: () => {
        if (
          !this.eventService.getCurrentEvent() ||
          this.eventDocumentId !== this.eventService.getCurrentEvent()?.documentId
        ) {
          this.eventService.clearCurrentEvent();
          throw Error(`Event with id ${this.eventDocumentId} not found`);
        }
      },
    });
  }

  public eventHasLinks(event: EventItem<EventType>): event is EventWithLinkItems<EventType> {
    return !!event && isEventWithLinks(event) && !!event.links && event.links.length > 0;
  }

  public getEventLinks(event: EventWithLinkItems<EventType>): LinksItemModel[] | undefined {
    return event.links;
  }
}
